import { msAPI, osAPI, manageAPI } from "./index.js";

export const getActiveOrders = async () => {
  return (await osAPI.get("merchant/orders/active")).data;
};

export const getPastOrders = async (page, limit, start, end, searchQuery = "", orderTypes = []) => {
  const params = {
    page,
    limit,
    start,
    end
  };
  
  if (searchQuery) {
    params.search = searchQuery;
  }
  // If "_all" is selected, don't send orderTypes (no filter)
  if (orderTypes && orderTypes.length > 0 && !orderTypes.includes("_all")) {
    params.orderTypes = orderTypes.join(",");
  }
  
  return (await osAPI.get("merchant/orders/past", { params })).data;
};

export const getCartByOrderID = async (id, userId, queries = {}) => {
  if (queries && Object.keys(queries).length > 0) {
    return (await msAPI.get(`merchant/cart/archive/${userId}/${id}/v2`, { params: queries })).data;
  }
  return (await msAPI.get(`merchant/cart/archive/${userId}/${id}/v2`)).data;
};

export const deleteItemFromCart = async (payload) => {
  return (await msAPI.delete(`merchant/cart/v2`, { data: payload })).data;
};

export const updateOrderCart = async (payload, repair = false) => {
  if (repair) {
    return (await msAPI.patch(`merchant/cart/v2?repair=1`, payload)).data;
  }
  return (await msAPI.patch(`merchant/cart/v2`, payload)).data;
};

export const saveOrderUpdate = async (id, payload, repair = false) => {
  if (repair) {
    return (await osAPI.patch(`merchant/order/${id}?repair=1`, payload)).data;
  }
  return (await osAPI.patch(`merchant/order/${id}`, payload)).data;
};

export const getOrderByID = async (id) => {
  return (await osAPI.get(`merchant/orders/${id}`)).data;
};

export const getOrderHistory = async (orderId) => {
  return (await osAPI.get(`merchant/orders/${orderId}/history`)).data;
};

export const getOrdersByCustomer = async (id, merchantId, storeId, page = 1, limit = 10, searchQuery = "", startDate = "", endDate = "") => {
  const params = {};
  
  if (merchantId) {
    params.merchantId = merchantId;
  }
  if (storeId) {
    params.storeId = storeId;
  }
  if (page) {
    params.page = page;
  }
  if (limit) {
    params.limit = limit;
  }
  if (searchQuery) {
    params.search = searchQuery;
  }
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  
  return (await manageAPI.get(`customers/${id}/orders`, { params })).data;
};

export const acceptOrder = async (id) => {
  return (await osAPI.post(`merchant/orders/${id}/accept`)).data;
};

export const completeOrder = async (id, force = false) => {
  if (force) {
    return (await osAPI.post(`merchant/orders/${id}/complete?force=${force === 'override' ? 2 : 1}`)).data;
  }
  return (await osAPI.post(`merchant/orders/${id}/complete`)).data;
};

export const cancelOrder = async (id, reason) => {
  if (reason) {
    return (await osAPI.post(`merchant/orders/${id}/cancel`, { reason })).data;
  }
  return (await osAPI.post(`merchant/orders/${id}/cancel`)).data;
};

export const refundOrder = async (orderId, amount, reason) => {
  return (await manageAPI.post(`orders/${orderId}/refund`, {
    amount: amount, // Amount in cents
    reason: reason
  })).data;
};

export const getAnalytics = async (startDate, endDate, aggregated) => {
  return (await msAPI.get(`merchant/analytics`, {
    params: { startDate, endDate, aggregated },
  })).data;
};

export const getPDFAnalytics = async (
  startDate,
  endDate,
  selectedLocations
) => {
  return (await osAPI.get(`merchant/orders/pdf`, {
    params: { startDate, endDate, sellers: selectedLocations.join(",") },
  })).data;
};

const triggerBlobDownload = (blob, contentDisposition, fallbackName = "download.csv") => {
  let filename = fallbackName;
  if (contentDisposition) {
    const matches = contentDisposition.match(/filename="([^"]+)"/);
    if (matches && matches[1]) {
      filename = matches[1];
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadCSV = async (start, end, selectedLocations, type) => {
  let apiUrl = "";
  switch (type) {
    case "past-orders":
      apiUrl = `merchant/orders/csv`;
      break;
    case "product-sales":
      apiUrl = `merchant/product-sales/csv`;
      break;
    case "customers":
      apiUrl = `merchant/customers/csv`;
      break;
    default:
      throw new Error("Invalid type");
  }
  try {
    const response = await osAPI.get(
      `${apiUrl}?start=${start}&end=${end}&sellers=${selectedLocations.join(",")}`,
      {
        responseType: "blob",
      }
    );
    triggerBlobDownload(
      response.data,
      response.headers["content-disposition"]
    );
  } catch (error) {
  }
};

export const downloadMpTaxCalculationCSV = async (start, end) => {
  const response = await msAPI.get(`admin/tax/mp-calculation/csv`, {
    params: { start, end },
    responseType: "blob",
    timeout: 600000, // large marketplace exports can take several minutes
  });
  triggerBlobDownload(
    response.data,
    response.headers["content-disposition"],
    "tax-calculation.csv"
  );
};

export const downloadMpAppFeeCSV = async (start, end) => {
  const response = await osAPI.get(`merchant/admin/app-fees/mp/csv`, {
    params: { start, end },
    responseType: "blob",
    timeout: 600000,
  });
  triggerBlobDownload(
    response.data,
    response.headers["content-disposition"],
    "app_fees_mp.csv"
  );
};

export const downloadDirectAppFeeCSV = async (start, end) => {
  const response = await osAPI.get(`merchant/admin/app-fees/direct/csv`, {
    params: { start, end },
    responseType: "blob",
    timeout: 600000,
  });
  triggerBlobDownload(
    response.data,
    response.headers["content-disposition"],
    "app_fees_direct.csv"
  );
};

export const downloadRevenueReconciliationXlsx = async (start, end) => {
  const response = await osAPI.get(`merchant/admin/revenue-reconciliation/xlsx`, {
    params: { start, end },
    responseType: "blob",
    timeout: 600000,
  });
  triggerBlobDownload(
    response.data,
    response.headers["content-disposition"],
    "revenue_reconciliation.xlsx"
  );
};

export const getSubstitutionRecommendation = async (storeId, skuId) => {
  return (await msAPI.get(`direct/${storeId}/substitution-recommendation/${skuId}`)).data;
};

export const rejectOrder = async (payload) => {
  return (await osAPI.post(`merchant/reject/v3`, payload)).data;
};

export const markOrderAsReturned = async (orderId) => {
  const returnedDate = new Date().toISOString();
  return (await osAPI.post(`merchant/order/returned`, {
    params: {
      id: orderId,
      returned: returnedDate
    }
  })).data;
};

export const generateDemoOrder = async () => {
  return (await osAPI.post(`merchant/order/mp/demo-order`)).data;
};
